import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  filter,
  forkJoin,
  from,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { EmbeddedTokenResponse } from './shared/interfaces/embedded-token-model';
import { ConfigService } from './shared/services/core/config.service';
import { ElementMenu } from './shared/interfaces/Menu/elementMenu.model';
import { MenuItem } from './shared/interfaces/Menu/menuItem.model';
import { UserDataService } from './shared/services/userData.service';
import { AppSkeletonComponent } from '@hygie31/ngx-hygie31-kit-ui-17';
import { ProgressSpinnerComponent } from './shared/components/progress-spinner/progress-spinner.component';

import packageJson from '../../package.json';
import { KeycloakService } from 'keycloak-angular';
import { PowerbiReportService } from './shared/services/powerbi-report.service';
import * as pbi from 'powerbi-client';
import { LoaderService } from './shared/services/loader.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { UserSessionService } from './shared/services/user.service';
import { UserEnseigneService } from './shared/services/userEnseigne.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
  imports: [AppSkeletonComponent, ProgressSpinnerComponent, CommonModule],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();

  readonly KEY_CURRENT_USER = 'currentUserCockpit';
  readonly KEY_ADMIN = 'adminCockpit';
  readonly title = 'Plateforme Adhérent - Hygie31';
  readonly appNameAzure = 'COCKPIT';
  readonly appNameTitle = 'Plateforme Adhérent';
  readonly legalMentionRouterLink = 'mention-legales';

  private readonly _dispatchToken = new Subject<EmbeddedTokenResponse>();
  private readonly _dispatchError = new Subject<boolean>();
  private readonly _cockpitTypeByActivity = {
    pharma: 'accueil',
    optique: 'cockpit-optique',
  };

  currentUser: BehaviorSubject<string>;
  embeddedToken$: Observable<EmbeddedTokenResponse>;
  error$: Observable<boolean>;

  firstname: string = '';
  lastname: string = '';
  activiteAdherent: string = '';
  filialeAdherent: string = '';

  env = environment.name;

  onlySideMenuIcon: boolean = true;
  version = packageJson.version;
  displayedApp: string[] = []; // Filter the apps that should be displayed in the app-selector
  urlRedirectionApp: { [key: string]: string } = {};
  menu: ElementMenu[] = [];

  menuConfig: Record<string, MenuItem[]> = {
    dashboard: [
      {
        icon: 'home',
        name: 'Accueil',
        route: '',
      },
    ],
    cockpit: [
      {
        icon: 'cockpit',
        name: 'Cockpit',
        route: 'cockpit',
      },
    ],
    cockpitoptique: [
      {
        icon: 'cockpitoptique',
        name: 'Cockpit Optique',
        route: 'cockpit/optique',
      },
    ],
    marge: [
      {
        icon: 'pilotagemarge',
        name: 'Marge',
        route: 'marge',
      },
    ],
    rentabilitelineaire: [
      {
        icon: 'pilotagerentabilite',
        name: 'Rentabilité',
        route: 'rentabilitelineaire',
      },
    ],
    ventes: [
      {
        icon: 'pilotagevente',
        name: 'Ventes',
        route: 'ventes',
      },
    ],
    stock: [
      {
        icon: 'pilotagestock',
        name: 'Stock',
        route: 'stock',
      },
    ],
    ranking: [
      {
        icon: 'ranking',
        name: 'Ranking',
        route: 'ranking',
      },
    ],
    pilotagefideliteofficine: [
      {
        icon: 'pilotagefideliteofficine',
        name: 'Fidélité officine',
        route: 'pilotagefideliteofficine',
      },
    ],
    pilotagefideliteclient: [
      {
        icon: 'pilotagefideliteclient',
        name: 'Fidélité client',
        route: 'pilotagefideliteclient',
      },
    ],
    pilotagepricing: [
      {
        icon: 'pilotagepricing',
        name: 'pricing',
        route: 'pilotagepricing',
      },
    ],
    suivilabo: [
      {
        icon: 'suivilabo',
        name: 'Suivi Labo',
        route: 'suivilabo',
      },
    ],
    operateurs: [
      {
        icon: 'pilotageoperateur',
        name: 'Opérateurs',
        route: 'operateurs',
      },
    ],
    trade: [
      {
        icon: 'pilotagetrade',
        name: 'Trade',
        route: 'trade',
      },
    ],
    simulationgenerique: [
      {
        icon: 'remisegeneriqueur',
        name: 'Simulation générique',
        route: 'simulation-generique',
      },
    ],
    simulationgrossiste: [
      {
        icon: 'remisegrossiste',
        name: 'Simulation grossiste',
        route: 'simulation-grossiste',
      },
    ],
    promotions: [
      {
        icon: 'pilotagetrade',
        name: 'Promotions',
        route: 'promotions',
      },
    ],
    mex: [
      {
        icon: 'mex',
        name: 'Mex',
        route: 'mex',
      },
    ],
    gestioncomptes: [
      {
        icon: 'gestioncomptes',
        name: 'Gestion des comptes',
        route: 'gestioncomptes',
      },
    ],
    gestionautorisations: [
      {
        icon: 'gestionautorisations',
        name: 'Gestion des autorisations',
        route: 'gestionautorisations',
      },
    ],
    servicedesante: [
      {
        icon: 'servicedesante',
        name: 'Service de santé',
        route: 'pilotageservicedesante',
      },
    ],
    biosimilaires: [
      {
        icon: 'biosimilaires',
        name: 'Biosimilaires',
        route: 'biosimilaires',
      },
    ],
    generiques: [
      {
        icon: 'generiques',
        name: 'Génériques',
        route: 'generiques',
      },
    ],
    pilotageaccordscommerciaux: [
      {
        icon: 'pilotagerfa',
        name: 'Pilotage Accords Commerciaux',
        route: 'pilotageaccordscommerciaux',
      },
    ],
  };

  constructor(
    private readonly router: Router,
    private readonly configService: ConfigService,
    private readonly keycloakService: KeycloakService,
    private readonly userDataService: UserDataService,
    private readonly powerbiService: PowerbiReportService,
    private readonly loader: LoaderService,
    private readonly userSessionService: UserSessionService,
    private userEnseigneService: UserEnseigneService,
    private cdr: ChangeDetectorRef,
  ) {
    this.embeddedToken$ = this._dispatchToken.asObservable();
    this.error$ = this._dispatchError.asObservable();
    this.currentUser = new BehaviorSubject('');
  }

  ngOnInit(): void {
    // Define redirection URLs for different apps
    this.urlRedirectionApp = {
      DECLAF: this.configService.config.modules.declaf,
      GESTAC: this.configService.config.modules.gestac,
      COCKPIT: this.configService.config.modules.cockpit,
      ADMIN: this.configService.config.modules.admin,
    };

    from(this.keycloakService.loadUserProfile()).subscribe(userDetails => {
      const groups = this.keycloakService.getUserRoles();

      this.setActiviteAdherent(groups);

      if (
        this.activiteAdherent === 'pharma' ||
        this.activiteAdherent === 'dsi' ||
        this.activiteAdherent === 'gpo'
      ) {
        this.setMenuByUserProfile(userDetails, groups);
        this.preloadHomeReport();
      }

      let user = {
        ...userDetails,
        groups,
      };

      this.userSessionService.filiale$
        .pipe(
          filter((f): f is string => f !== null),
          distinctUntilChanged(),
          takeUntil(this._destroying$),
        )
        .subscribe(filiale => {
          this.filialeAdherent = filiale.toLowerCase();
          this.cdr.markForCheck();
        });

      this.setCurrentUser(JSON.stringify(user));
      this.loadFiliales(user.email);
      // Convert the displayed app string to an array of app.
      this.feedDisplayedApp(groups);
    });
  }

  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Logs out the user from the application.
   * @return {void}
   */
  onLogout(): void {
    this.keycloakService.logout();
  }

  /**
   * Détermine l'activité adhérent selon les groupes Keycloak.
   * - Si uniquement "pfa." présent : pharma
   * - Si uniquement "pfa-opt." présent : optique
   * - Si les deux présents : dsi
   */
  private setActiviteAdherent(groups: string[]): void {
    const hasPharma = groups.some(g => g.startsWith('pfa.'));
    const hasOptique = groups.some(g => g.startsWith('pfa-opt.'));
    const hasGpo = groups.some(g => g.startsWith('pfa-gpo.'));

    const isGpoResponsable = groups.includes('pfa-gpo.responsable');
    const isGpoTitulaire = groups.includes('pfa-gpo.titulaire');
    const isGpoAdherent = groups.includes('pfa-gpo.adherent');

    if (hasPharma && !hasOptique) {
      this.activiteAdherent = 'pharma';
    } else if (hasPharma && hasOptique) {
      this.activiteAdherent = 'dsi';
    } else if (hasGpo) {
      this.activiteAdherent = 'gpo';
    } else if (hasOptique) {
      this.activiteAdherent = 'optique';
      this.navigateToCockpitOptique();
    } else {
      this.activiteAdherent = '';
    }
    this.userSessionService.setActivite(this.activiteAdherent);

    this.userSessionService.setGpoRole({
      isResponsable: isGpoResponsable,
      isTitulaire: isGpoTitulaire,
      isAdherent: isGpoAdherent,
    });
  }

  private loadFiliales(email: string | undefined): void {
    if (!email) {
      return;
    }

    this.userSessionService.gpoRole$
      .pipe(
        takeUntil(this._destroying$),
        switchMap(gpoRole => {
          const calls: Observable<string[]>[] = [];

          if (gpoRole.isResponsable) {
            calls.push(
              this.userEnseigneService.fetchEnseignesByEmail(email).pipe(catchError(() => of([]))),
            );
          }

          if (gpoRole.isTitulaire || gpoRole.isAdherent) {
            calls.push(
              this.userSessionService.getOfficine(email, 'FilialeSelector').pipe(
                map(adherents => adherents?.map(a => a.filiale).filter(Boolean) ?? []),
                catchError(() => of([])),
              ),
            );
          }

          if (calls.length === 0) {
            return of([]);
          }

          return forkJoin(calls).pipe(map(results => [...new Set(results.flat())]));
        }),
      )
      .subscribe(filiales => {
        if (filiales.length > 0) {
          const selected = filiales[0];

          this.userSessionService.setFiliale(selected);

          this.filialeAdherent = selected.toLowerCase();
        }
      });
  }

  /**
   * Sets the current user in the app and stores it in the session storage.
   * @param currentUser The current user to set.
   */
  private setCurrentUser(currentUser: string) {
    this.currentUser.next(currentUser);
    localStorage[this.KEY_CURRENT_USER] = currentUser;
    this.userDataService.updateUserData(currentUser);
  }

  /**
   * Sets the menu items based on the user's profile and user role flags.
   *
   * Dynamically constructs a menu by considering the user's profile and role flags. The menu options
   * will vary depending on the user's role, such as Catalogue Manager, Hygie31, Supplier, or ADV.
   *
   * @param {Object} user - The user object representing the user's profile.
   * @param groups
   * @param {boolean} user.isCatalogueManager - Indicates if the user is a Catalogue Manager.
   * @param {boolean} user.isHygie31 - Indicates if the user is a Catalogue Manager Hygie31.
   * @param {boolean} user.isFournisseur - Indicates if the user is a Supplier.z
   * @param {boolean} user.isADV - Indicates if the user is an ADV.
   */
  private setMenuByUserProfile(user: any, groups: string[]): void {
    const userMenus: MenuItem[] = [];

    this.firstname = user.firstName;
    this.lastname = user.lastName;

    const menuMapTitulaireAdmin: Record<string, string[]> = {
      'pfa.visualiser-accueil': ['dashboard'],
      'pfa-gpo.visualiser-accueil': ['dashboard'],
      'pfa.piloter-cockpit': ['cockpit'],
      'pfa-gpo.piloter-cockpit': ['cockpit'],
      'pfa-opt.piloter-cockpit': ['cockpitoptique'],
      'pfa.piloter-marge': ['marge'],
      'pfa.piloter-rentabilite-lineaire': ['rentabilitelineaire'],
      'pfa.piloter-ventes': ['ventes'],
      'pfa.piloter-stocks': ['stock'],
      'pfa.piloter-ranking': ['ranking'],
      'pfa.piloter-fidelite-officine': ['pilotagefideliteofficine'],
      'pfa.piloter-fidelite-client': ['pilotagefideliteclient'],
      'pfa.piloter-pricing': ['pilotagepricing'],
      'pfa.piloter-suivi-labo': ['suivilabo'],
      'pfa.piloter-operateurs': ['operateurs'],
      'pfa.piloter-trade': ['trade'],
      'pfa.remise-generique': ['simulationgenerique'],
      'pfa.remise-grossiste': ['simulationgrossiste'],
      'pfa-gpo.remise-generique': ['simulationgenerique'],
      'pfa-gpo.remise-grossiste': ['simulationgrossiste'],
      'pfa.piloter-promotion': ['promotions'],
      'pfa.piloter-mex': ['mex'],
      'pfa.piloter-service-sante': ['servicedesante'],
      'pfa.piloter-biosimilaires': ['biosimilaires'],
      'pfa.piloter-generiques': ['generiques'],
      'pfa.pilotage-accords-commerciaux': ['pilotageaccordscommerciaux'],
    };

    this.addAdminMenuItems(groups, menuMapTitulaireAdmin);

    const menuMap: Record<string, string[]> = {
      ...menuMapTitulaireAdmin,
      'pfa.administrer-gestion-comptes': ['gestioncomptes'],
      'pfa-gpo.administrer-gestion-comptes': ['gestioncomptes'],
      'pfa-gpo.administrer-autorisations': ['gestionautorisations'],
    };
    this.addMenuItemsByGroups(groups, userMenus, menuMap);

    // Update the menu property with the generated user-specific menu.
    this.menu = userMenus;
  }

  /**
   * Adds menu items based on the user's groups.
   * @param groups - The user's groups.
   * @param userMenus - The array to add the menu items to.
   * @param menuMap - The map of menu items.
   */
  private addMenuItemsByGroups(
    groups: string[],
    userMenus: MenuItem[],
    menuMap: Record<string, string[]>,
  ): void {
    // Parcourir chaque groupe et ajouter les noms de menu correspondants
    for (const key in menuMap) {
      if (groups?.includes(key)) {
        const menuNames = menuMap[key];
        menuNames.forEach(menuName => {
          const menuItems = this.menuConfig[menuName];
          if (menuItems) {
            userMenus.push(...menuItems);
          }
        });
      }
    }
  }

  /**
   * Adds admin menu items based on the user's groups.
   * @param groups - The user's groups.
   * @param menuMapTitulaireAdmin - The map of menu items for the admin.
   */
  private addAdminMenuItems(
    groups: string[],
    menuMapTitulaireAdmin: Record<string, string[]>,
  ): void {
    if (
      groups.includes('pfa.administrer-gestion-comptes') ||
      groups.includes('pfa-gpo.administrer-gestion-comptes') ||
      groups.includes('pfa-gpo.administrer-autorisations')
    ) {
      localStorage[this.KEY_ADMIN] = [];
      let adminItems: { role: string; name: string }[] = [];
      for (const key in menuMapTitulaireAdmin) {
        if (groups?.includes(key)) {
          const menuNames = menuMapTitulaireAdmin[key];
          menuNames.forEach(menuName => {
            const menuItems = this.menuConfig[menuName];
            if (menuItems) {
              menuItems.forEach(menuItem => {
                adminItems.push({ role: key, name: menuItem.name });
              });
            }
          });
        }
      }
      localStorage[this.KEY_ADMIN] = JSON.stringify(adminItems);
    }
  }

  /**
   * Sets the displayed app based on the given roles.
   * @param {string[]} roles - An array of roles.
   * @return {void}
   */
  private feedDisplayedApp(roles: string[]): void {
    let distinctRoles = roles
      .map(role => role.split('.')[0])
      .filter((role, index, self) => self.indexOf(role) === index);

    this.displayedApp = [];

    const roleAppMap: any = {
      'gestac': 'Gestac',
      'pfa': 'Cockpit',
      'pfa-gpo': 'Cockpit',
      'app-admin': 'Admin',
      'app-admin-gpo': 'Admin',
      'declaf': 'Declaf',
    };

    for (const role of distinctRoles) {
      if (roleAppMap[role]) {
        this.displayedApp.push(roleAppMap[role]);
      }
    }
  }

  /**
   * Preloads the home Power BI report configuration on initial app load.
   *
   * This method generates an embed token for the "accueil" report type,
   * and caches the corresponding embed configuration for future use.
   * It avoids embedding or rendering the report immediately to improve performance.
   *
   * If the configuration is already cached (based on the token), it skips the operation.
   *
   * Errors during token generation are logged to the console.
   */
  preloadHomeReport() {
    let type = this.setHomeReportType();

    this.powerbiService.generateToken(type).subscribe({
      next: (token: EmbeddedTokenResponse) => {
        this.loader.setLoading(false);
        const tokenKey = token.EmbedToken.Token;
        if (this.powerbiService.getCachedConfig(tokenKey)) return;

        const config: pbi.IEmbedConfiguration = {
          type: 'report',
          tokenType: pbi.models.TokenType.Embed,
          accessToken: token.EmbedToken.Token,
          embedUrl: token.EmbedReport[0].EmbedUrl,
          id: token.EmbedReport[0].ReportId,
          permissions: pbi.models.Permissions.All,
          viewMode: pbi.models.ViewMode.View,
          settings: {
            navContentPaneEnabled: false,
            filterPaneEnabled: false,
          },
        };

        this.powerbiService.cacheConfig(tokenKey, config);
      },
      error: err => {
        console.error('Error preloading PowerBI home report:', err);
      },
    });
  }

  private setHomeReportType(): string {
    let type = this._cockpitTypeByActivity.pharma;

    if (this.activiteAdherent === 'optique') {
      type = this._cockpitTypeByActivity.optique;
    }

    localStorage['cockpitType'] = type;
    return type;
  }

  private navigateToCockpitOptique() {
    localStorage.setItem('cockpitType', this._cockpitTypeByActivity.optique);
    this.router.navigate(['cockpit/optique']);
  }
}
